import { Routes } from '@angular/router';
import { AdminLayout } from '../layouts/admin-layout/admin-layout';
import { authGuard } from '../core/guards/auth.guard';
import { DashboardRedirector } from '../features/dashboard-redirector';
import { ClinicalDashboard } from '../features/clinical-dashboard/clinical-dashboard';
import { SystemDashboard } from '../features/system-dashboard/system-dashboard';
import { BusinessList } from '../features/business/business-list/business-list';
import { BusinessForm } from '../features/business/business-form/business-form';
import { MyBusiness } from '../features/my-business/my-business';
import { UserList } from '../features/users/user-list/user-list';
import { UserForm } from '../features/users/user-form';
import { SelectBusiness } from '../features/select-business/select-business';
import { SystemModuleList } from '../features/system-modules/system-module-list/system-module-list';
import { SystemModuleForm } from '../features/system-modules/system-module-form/system-module-form';
import { StaffList } from '../features/staff/staff-list/staff-list';
import { StaffForm } from '../features/staff/staff-form/staff-form';
import { MembershipAdminList } from '../features/membership/membership-admin-list/membership-admin-list';
import { permissionGuard } from '../core/guards/permission.guard';

export const ADMIN_ROUTES: Routes = [
    {
        path: 'app',
        component: AdminLayout,
        canActivate: [authGuard],
        children: [
            // ── Business selector (dentro del shell con sidebar visible) ──
            { path: 'select-business', component: SelectBusiness },
            { path: 'dashboard', component: DashboardRedirector },
            { 
              path: 'clinical-dashboard', 
              component: ClinicalDashboard,
              canActivate: [permissionGuard],
              data: { permission: 'dashboard:list' }
            },
            { 
              path: 'system-dashboard', 
              component: SystemDashboard,
              canActivate: [permissionGuard],
              data: { permission: 'system:dashboard' }
            },
            { path: 'my-business', component: MyBusiness },
            {
                path: 'business',
                canActivate: [permissionGuard],
                data: { permission: 'business:list' },
                children: [
                    { path: '', component: BusinessList },
                    { path: 'new', component: BusinessForm },
                    { path: 'edit/:id', component: BusinessForm },
                    { path: 'membership/:id', loadComponent: () => import('../features/membership/membership-admin-detail/membership-admin-detail').then(m => m.MembershipAdminDetail) },
                    { path: 'edit/:id/staff/new', component: StaffForm },
                    { path: 'edit/:id/staff/edit/:staffId', component: StaffForm }
                ]
            },
            {
                path: 'users',
                canActivate: [permissionGuard],
                data: { permission: 'users:list' },
                children: [
                    { path: '', component: UserList },
                    { path: 'new', component: UserForm },
                    { path: 'edit/:id', component: UserForm }
                ]
            },
            {
                path: 'staff',
                canActivate: [permissionGuard],
                data: { permission: 'staff:list' },
                children: [
                    { path: '', component: StaffList },
                    { path: 'new', component: StaffForm },
                    { path: 'edit/:id', component: StaffForm }
                ]
            },
            // ── System Modules (Solo SuperAdmin) ─────────────────────────
            {
                path: 'system-modules',
                canActivate: [permissionGuard],
                data: { permission: 'system-modules:list' },
                children: [
                    { path: '', component: SystemModuleList },
                    { path: 'new', component: SystemModuleForm },
                    { path: 'edit/:id', component: SystemModuleForm }
                ]
            },
            {
                path: 'appointments',
                canActivate: [permissionGuard],
                data: { permission: 'appointments:list' },
                children: [
                    { path: '', loadComponent: () => import('../features/appointments/appointment-list/appointment-list').then(m => m.AppointmentList) },
                    { path: 'calendar', loadComponent: () => import('../features/appointments/app-calendar/app-calendar').then(m => m.AppointmentCalendar) },
                    { path: 'my-agenda', data: { myAgenda: true }, loadComponent: () => import('../features/appointments/app-calendar/app-calendar').then(m => m.AppointmentCalendar) },
                    { path: 'new', loadComponent: () => import('../features/appointments/appointment-form/appointment-form').then(m => m.AppointmentForm) },
                    { path: ':id', loadComponent: () => import('../features/appointments/appointment-form/appointment-form').then(m => m.AppointmentForm) }
                ]
            },
            {
                path: 'patients',
                canActivate: [permissionGuard],
                data: { permission: 'patients:list' },
                children: [
                    { path: '', loadComponent: () => import('../features/patients/patient-list/patient-list').then(m => m.PatientList) },
                    { path: 'new', loadComponent: () => import('../features/patients/patient-form/patient-form').then(m => m.PatientForm) },
                    { path: ':id', loadComponent: () => import('../features/patients/patient-form/patient-form').then(m => m.PatientForm) },
                    { path: 'record/:id', loadComponent: () => import('../features/medical-records/record-view/record-view').then(m => m.RecordView) }
                ]
            },
            {
                path: 'medical-records',
                canActivate: [permissionGuard],
                data: { permission: 'medical-records:list' },
                children: [
                    {
                        path: '',
                        loadComponent: () => import('../features/medical-records/medical-record-list/medical-record-list').then(m => m.MedicalRecordList)
                    },
                    {
                        path: 'new',
                        loadComponent: () => import('../features/medical-records/medical-record-setup/medical-record-setup').then(m => m.MedicalRecordSetup)
                    },
                    {
                        path: 'record/:id',
                        loadComponent: () => import('../features/medical-records/record-view/record-view').then(m => m.RecordView)
                    }
                ]
            },
            {
                path: 'inventory',
                canActivate: [permissionGuard],
                data: { permission: 'inventory:view' },
                loadComponent: () => import('../features/inventory/catalog/inventory-catalog').then(m => m.InventoryCatalog)
            },
            {
                path: 'billing',
                canActivate: [permissionGuard],
                data: { permission: 'billing:list' },
                children: [
                    { path: '', loadComponent: () => import('../features/billing/billing-dashboard/billing-dashboard').then(m => m.BillingDashboard) },
                    { path: 'manage/:id', loadComponent: () => import('../features/billing/manage-payment/manage-payment').then(m => m.ManagePayment) }
                ]
            },
            {
                path: 'invoices',
                canActivate: [permissionGuard],
                data: { permission: 'invoices:list' },
                loadComponent: () => import('../features/billing/invoice-list/invoice-list').then(m => m.InvoiceList)
            },
            {
                path: 'payment-methods',
                canActivate: [permissionGuard],
                data: { permission: 'payment-methods:list' },
                loadComponent: () => import('../features/billing/payment-methods/payment-methods').then(m => m.PaymentMethods)
            },
            {
                path: 'profile',
                loadComponent: () => import('../features/profile/profile.component').then(m => m.ProfileComponent)
            },
            {
                path: 'notifications',
                loadComponent: () => import('../features/notifications/notifications.component').then(m => m.NotificationsComponent)
            },
            {
                path: 'membership',
                canActivate: [permissionGuard],
                data: { permission: 'memberships:view' },
                loadComponent: () => import('../features/membership/membership-wrapper').then(m => m.MembershipWrapper)
            },
            {
                path: 'my-membership',
                canActivate: [permissionGuard],
                data: { permission: 'memberships:view' },
                loadComponent: () => import('../features/membership/membership-wrapper').then(m => m.MembershipWrapper)
            },
            {
                path: 'system-memberships',
                canActivate: [permissionGuard],
                data: { permission: 'memberships:list' },
                component: MembershipAdminList
            },
            {
                path: 'system-payment-methods',
                canActivate: [permissionGuard],
                data: { permission: 'payment-methods-sys:list', isSystem: true },
                loadComponent: () => import('../features/billing/payment-methods/payment-methods').then(m => m.PaymentMethods)
            },
            {
                path: 'admin/payments',
                canActivate: [permissionGuard],
                data: { permission: 'payment-system:list-request' },
                loadComponent: () => import('../features/payment-admin/payment-admin').then(m => m.PaymentAdminComponent)
            },
            {
                path: 'procedures',
                canActivate: [permissionGuard],
                data: { permission: 'procedures:list' },
                children: [
                    {
                        path: '',
                        loadComponent: () => import('../features/procedures/procedures-list/procedures-list').then(m => m.ProceduresList)
                    }
                ]
            },
            {
                path: 'reports',
                canActivate: [permissionGuard],
                data: { permission: 'reports:list' },
                loadComponent: () => import('../features/reports/reports-dashboard/reports-dashboard').then(m => m.ReportsDashboard)
            },
            {
                path: 'consents',
                canActivate: [permissionGuard],
                data: { permission: 'consents:list' },
                loadComponent: () => import('../features/consents/management/consent-management').then(m => m.ConsentManagement)
            }
        ]
    }
];
